import { Draft, produce } from 'immer';

import {
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP_ALL_STATE_CLEAR,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
  CONTRACT_SETUP_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
  CONTRACT_SETUP_FIELD_CONTEXT_MENU_CHANGE,
  CONTRACT_SETUP_FIELD_CREATE,
  CONTRACT_SETUP_FIELD_REMOVE,
  CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
} from '@app/store/actionTypes';
import CheckboxGroupInputMode from '@app/store/CheckboxGroupInputMode';
import { ContractSetupAction } from '@app/store/contract-setup/actions';
import FieldInformation from '@app/store/contract-setup/ContractSetupFieldInformation';
import FieldPropType from '@app/store/contract-setup/ContractSetupFieldPropType';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import TextAlign from '@app/store/TextAlign';

export interface ContractSetupFieldsInformationState {
  [id: string]: FieldInformation;
}

const initialState: ContractSetupFieldsInformationState = {};

const reducer = produce(
  (
    draft: Draft<ContractSetupFieldsInformationState>,
    action: ContractSetupAction
  ): Draft<ContractSetupFieldsInformationState> => {
    switch (action.type) {
      case CONTRACT_SETUP_ALL_STATE_CLEAR:
      case CONTRACT_SETUP_BY_DRAFT_REQUEST:
      case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST:
        return initialState;
      case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS: {
        const { participantFields } = action.payload;
        participantFields.forEach((participantField) => {
          const { fieldId, participantId, dataLabel } = participantField;
          if (participantField.fieldType === FieldType.CHECKBOX_GROUP) {
            draft[participantField.fieldId] = {
              uuid: participantField.fieldId,
              dataLabel: participantField.dataLabel,
              participantId,
              required: participantField.guide.required,
              checkboxCount: participantField.checkboxNumber,
              checkboxGroupMode: participantField.checkboxGroupMode,
              type: FieldType.CHECKBOX_GROUP,
            };

            participantField.children.forEach((checkboxField) => {
              draft[checkboxField.fieldId] = {
                dataLabel: checkboxField.dataLabel,
                index: checkboxField.index,
                uuid: checkboxField.fieldId,
                participantId,
                type: checkboxField.fieldType,
              };
            });
          } else if (participantField.fieldType === FieldType.SIGNATURE_FIELD) {
            const { allowSignatureTypes } = participantField.guide;

            draft[fieldId] = {
              allowSignatureTypes,
              dataLabel,
              participantId,
              type: FieldType.SIGNATURE_FIELD,
              uuid: fieldId,
            };
          } else if (participantField.fieldType === FieldType.TEXT_FIELD) {
            const { required, textStyleAlign, textStyleFontSize, textStyleFontType } =
              participantField.guide;
            draft[fieldId] = {
              dataLabel,
              fontFamily: textStyleFontType,
              fontSize: textStyleFontSize,
              participantId,
              required,
              textAlign: textStyleAlign,
              type: FieldType.TEXT_FIELD,
              uuid: fieldId,
            };
          }
        });
        return draft;
      }
      case CONTRACT_SETUP_FIELD_CONTEXT_MENU_CHANGE: {
        const { id, propType, value } = action.payload;

        const targetField = draft[id];

        if (propType === FieldPropType.DATA_LABEL) {
          targetField.dataLabel = value as string;
        }

        if (
          propType === FieldPropType.TEXT_FIELD_ALIGN &&
          targetField.type === FieldType.TEXT_FIELD
        ) {
          targetField.textAlign = value as TextAlign;
        }

        if (
          propType === FieldPropType.TEXT_FIELD_FONT_FAMILY &&
          targetField.type === FieldType.TEXT_FIELD
        ) {
          targetField.fontFamily = value as FontType;
        }

        if (
          propType === FieldPropType.TEXT_FIELD_FONT_SIZE &&
          targetField.type === FieldType.TEXT_FIELD
        ) {
          targetField.fontSize = Number(value);
        }

        if (propType === FieldPropType.REQUIRED && targetField.type === FieldType.TEXT_FIELD) {
          targetField.required = Boolean(value);
        }

        if (propType === FieldPropType.REQUIRED && targetField.type === FieldType.CHECKBOX_GROUP) {
          targetField.required = Boolean(value);
        }

        if (
          propType === FieldPropType.CHECKBOX_COUNT &&
          targetField.type === FieldType.CHECKBOX_GROUP
        ) {
          targetField.checkboxCount = Number(value);
        }

        if (
          propType === FieldPropType.CHECKBOX_GROUP_MODE &&
          targetField.type === FieldType.CHECKBOX_GROUP
        ) {
          targetField.checkboxGroupMode = value as CheckboxGroupInputMode;
        }

        return draft;
      }
      case CONTRACT_SETUP_FIELD_CREATE: {
        const {
          meta: { uuid },
          information,
        } = action.payload;

        draft[uuid] = information;

        /**
         * 필드 생성할 때 텍스트 정렬 기본값은 왼쪽(LEFT)입니다.
         */
        const target = draft[uuid];
        if (target.type === FieldType.TEXT_FIELD) {
          target.textAlign = TextAlign.LEFT;
        }

        return draft;
      }
      case CONTRACT_SETUP_FIELD_REMOVE: {
        const { uuid } = action.payload;
        const { type } = draft[uuid];
        if (type === FieldType.SIGNATURE_FIELD) {
          delete draft[uuid];
        }
        return draft;
      }

      case CONTRACT_SETUP_BY_DRAFT_SUCCESS: {
        const { participants } = action.payload;
        participants.forEach((participant) => {
          const { fields, id: participantId } = participant;

          fields.forEach((field) => {
            switch (field.type) {
              case FieldType.CHECKBOX_GROUP:
                draft[field.id] = {
                  uuid: field.id,
                  dataLabel: field.dataLabel,
                  participantId,
                  required: field.required,
                  checkboxCount: field.checkboxNumber,
                  checkboxGroupMode: field.checkboxGroupMode,
                  type: FieldType.CHECKBOX_GROUP,
                };

                field.children.forEach((checkboxField) => {
                  draft[checkboxField.id] = {
                    dataLabel: checkboxField.dataLabel,
                    index: checkboxField.index,
                    uuid: checkboxField.id,
                    participantId,
                    type: FieldType.CHECKBOX_FIELD,
                  };
                });

                break;
              case FieldType.SIGNATURE_FIELD:
                draft[field.id] = {
                  allowSignatureTypes: field.signatureTypes,
                  dataLabel: field.dataLabel,
                  participantId,
                  type: FieldType.SIGNATURE_FIELD,
                  uuid: field.id,
                };
                break;
              case FieldType.TEXT_FIELD:
                draft[field.id] = {
                  dataLabel: field.dataLabel,
                  fontFamily: field.textStyle.font,
                  fontSize: field.textStyle.size,
                  participantId,
                  required: field.required,
                  textAlign: field.textStyle.align,
                  type: FieldType.TEXT_FIELD,
                  uuid: field.id,
                };
                break;
              default:
                break;
            }
          });
        });
        return draft;
      }
      case CONTRACT_SETUP_FIELD__ALLOW_SIGNATURE_TYPES_SELECT: {
        const { allowSignatureTypes, participantId } = action.payload;

        Object.values(draft).forEach((value) => {
          if (value.type === FieldType.SIGNATURE_FIELD && value.participantId === participantId) {
            value.allowSignatureTypes = allowSignatureTypes;
          }
        });

        return draft;
      }
      case CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE: {
        const { fieldIds } = action.payload;
        fieldIds.forEach((fieldId) => {
          if (fieldId in draft) {
            delete draft[fieldId];
          }
        });
        return draft;
      }
      default:
        return draft;
    }
  },
  initialState
);

export default reducer;
